<template>
  <div class="ace-grain-beans-step-3 ace-content">
    <Header :images="feedMaterial.images" :title="feedMaterial.title"/>
    <h2 class="ace-grain-beans-step-3__title">Your results</h2>
    <div class="ace-grain-beans-step-3__summary">
      Treating {{ formatNumber(feedMaterial.weight) }} tonnes of {{ feedMaterial.name }} at a
      moisture content of {{ percent(feedMaterial.moisture) }} results in estimated carbon
      savings of <strong>{{ savingsOnDried }} tonnes CO2-eq</strong> compared to drying grain
      with {{ fuelName }}<span v-if="feedMaterial.name === 'beans' || feedMaterial.moisture > 17.5">.</span>
      <span v-if="(feedMaterial.name === 'wheat' || feedMaterial.name === 'barley') && feedMaterial.moisture < 17.5">
         and <strong>{{ savingsOnAerated }} tonnes CO2-eq</strong> compared to
      aeration alone.
      </span>
      Savings are created by the excellent animal performance attributed to treated
      {{ feedMaterial.name }}, the displacement of thermal energy and a reduced electrical
      requirement for on-going aeration during storage.
    </div>
    <div class="ace-grain-beans-step-3__comparison">
      <h3 class="ace-grain-beans-step-3__subtitle">Carbon footprint comparison</h3>
      <div class="ace-grain-beans-step-3__chart-totals">
        <div v-for="(total, index) in chartData.totals" :key="index">{{ total || 0 }}</div>
      </div>
      <Chart :data="chartData"/>
    </div>
    <div class="ace-grain-beans-step-3__breakdown">
      <h3 class="ace-grain-beans-step-3__subtitle">Breakdown of carbon sources</h3>
      <div class="ace-grain-beans-step-3__breakdown">
        <p class="ace-grain-beans-step-3__breakdown-axis">Components</p>
        <table>
          <thead>
          <tr>
            <th>&nbsp;</th>
            <th class="spacer"></th>
            <th v-for="(label, index) in chartData.labels" :key="index">{{ label }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(series, index) in chartData.datasets" :key="index">
            <th scope="col">
              <span :style="{ backgroundColor: series.backgroundColor }"></span>
              {{ series.label }}
            </th>
            <td class="spacer"></td>
            <td v-for="(data, index) in series.data" :key="index">{{ data || 0 }}</td>
          </tr>
          </tbody>
          <tfoot>
          <tr>
            <th scope="col">TOTAL</th>
            <td class="spacer"></td>
            <td v-for="(total, index) in chartData.totals" :key="index">{{ total || 0 }}</td>
          </tr>
          </tfoot>
        </table>
        <p class="ace-grain-beans-step-3__breakdown-message">
          Carbon emissions measured as kg CO2-eq/tonne DM
        </p>
      </div>
    </div>
    <ButtonGroup class="ace-grain-beans-step-3__actions">
      <Button variant="dark" icon="arrow" text="Download PDF" @click="downloadPDF"/>
      <Button variant="dark" icon="arrow" text="Email me the results" @click="toggleModal"/>
    </ButtonGroup>
    <Modal class="ace-grain-beans-step-3__modal" :visible="modalVisible" @close="toggleModal">
      <Form ref="form" :customer="customer" @submit="emailPDF" @close="toggleModal"/>
    </Modal>
    <SmartMilling class="ace-grain-beans-step-3__cta"/>
  </div>
</template>

<script>
import axios from 'axios';
import {format} from 'date-fns';

import Chart from './Chart.vue';

import Modal from '../Layout/Modal.vue';
import Header from '../Layout/Header.vue';
import ButtonGroup from '../Layout/ButtonGroup.vue';
import Button from '../Layout/Button.vue';

import Form from '../Shared/Form.vue';
import SmartMilling from '../Shared/SmartMilling.vue';

import {round, percent, formatNumber} from '../../helpers';

export default {
  data() {
    return {
      axios: axios.create({
        baseURL: 'https://adesco.ie/api',
         // baseURL: 'http://localhost:8000/api',
        timeout: 30000,
      }),
      customer: {
        fullName: '',
        businessName: '',
        businessEmail: '',
        country: 'Ireland',
        phone: '',
        gdprOptIn: false,
      },
      status: null,
      modalVisible: false,
    };
  },
  props: ['chartData', 'efBreakdown', 'feedMaterial', 'fuel', 'fuelName', 'resultsToShow'],



  methods: {
    round,
    percent,
    formatNumber,
    toggleModal() {
      this.modalVisible = !this.modalVisible;

      this.$refs.form.$children[0].reset();

      if (!this.modalVisible) {
        this.customer = {
          fullName: '',
          businessName: '',
          businessEmail: '',
          country: 'Ireland',
          phone: '',
          gdprOptIn: false,
        };
      }
    },
    downloadPDF() {
      const self = this;

      this.status = 'working';

      this.axios
          .post('grain-beans/v1/download', this.payload, {
            responseType: 'arraybuffer',
            headers: {
              Accept: 'application/pdf',
            },
          })
          .then(resp => {
            const blob = new Blob([resp.data], {type: 'application/pdf'});
            const link = document.createElement('a');

            const date = format(new Date(), 'yyyyMMdd_HHmmss');

            link.href = window.URL.createObjectURL(blob);
            link.download = `adesco_${date}.pdf`;
            link.click();

            self.status = 'done';
          })
          .catch(err => {
            console.log(err);
            self.status = 'error';
          });
    },
    emailPDF() {
      const self = this;

      this.status = 'working';

      this.axios
          .post('grain-beans/v1/email', this.payload, {
            responseType: 'arraybuffer',
            headers: {
              Accept: 'application/pdf',
            },
          })
          .then(() => {
            setTimeout(this.toggleModal, 5000);

            self.status = 'done';
          })
          .catch(err => {
            console.log(err);
            self.status = 'error';
          });
    },
  },
  computed: {
    savingsOnDried() {
      return round(
          this.efBreakdown[this.fuel].annualFootprint - this.efBreakdown.adesco.annualFootprint,
          1
      );
    },
    savingsOnAerated() {
      return round(
          this.efBreakdown.aerated.annualFootprint - this.efBreakdown.adesco.annualFootprint,
          1
      );
    },
    report() {
      const {
        chartData,
        efBreakdown,
        feedMaterial,
        fuel,
        fuelName,
        resultsToShow,
        savingsOnDried,
        savingsOnAerated,
      } = this;

      const {datasets, labels, totals} = chartData;

      const report = {
        chartData: {
          datasets: datasets.map(dataset =>
              Object.keys(dataset)
                  .filter(key => key !== '_meta')
                  .reduce((obj, key) => {
                    const arr = obj;
                    arr[key] = dataset[key];
                    return arr;
                  }, {})
          ),
          labels,
          totals,
        },
        efBreakdown,
        feedMaterial,
        fuel,
        fuelName,
        resultsToShow,
        savingsOnDried,
        savingsOnAerated,
      };

      return report;
    },
    payload() {
      const {customer, report} = this;

      const payload = {
        customer,
        report,
      };

      return payload;
    },
  },
  components: {
    Chart,
    Header,
    ButtonGroup,
    Button,
    Modal,
    Form,
    SmartMilling,
  },
};
</script>

 <style scoped lang="scss">
@import '~@/assets/styles/mixins';

.ace-grain-beans-step-3 {
  margin-left: auto;
  margin-right: auto;
  max-width: 1030px;

  @media screen and (min-width: 767px) {
    padding: 0 30px;
  }

  &__title,
  &__summary,
  &__comparison {
    margin: 0 auto;
    max-width: 770px;
  }

  &__title {
    margin-top: 20px;

    font-family: Chambers;
    font-size: 40px;
  }

  &__subtitle {
    @include title;

    margin-bottom: 55px;
    margin-top: 55px;
  }

  &__chart-totals {
    padding-bottom: 10px;
    padding-left: 58px;

    display: flex;

    font-size: 18px;

    & > div {
      flex: 1;
    }
  }

  &__summary {
    margin-top: 30px;

    font-size: 20px;
    font-weight: 300;
  }

  &__breakdown {
    margin: 0 auto;
    max-width: 650px;

    position: relative;

    table {
      width: 100%;

      font-size: 18px;
    }

    th,
    td {
      padding: 5px 10px;

      &.spacer {
        max-width: 15px;
      }
    }

    thead,
    tbody {
      th {
        text-transform: uppercase;

        font-size: 11px;
        font-weight: 400;
      }
    }

    tbody,
    tfoot {
      th {
        text-align: left;

        border-left-width: 8px;

        max-width: 150px;

        display: flex;

        align-items: center;

        span {
          height: 32px;
          margin-right: 10px;

          display: inline-block;

          flex: 8px 0 0;
        }
      }

      td {
        border-right: 1px solid #707070;
        border-top: 1px solid #707070;
        @media screen and (min-width: 767px) {
          padding: 0 60px;
        }
      }
    }

    tbody {
      tr:first-child {
        td {
          border-top: 0;
        }
      }
    }

    &-axis {
      transform: rotate(-90deg) translateX(30px);

      margin: 0;

      display: none;

      left: -60px;
      position: absolute;
      top: 50%;

      font-size: 18px;
      @media screen and (min-width: 767px) {
        display: block;
      }
    }

    &-message {
      text-align: left;

      padding: 0 15px;

      font-size: 12px;
      font-weight: 300;
    }
  }

  &__actions {
    margin-top: 55px;
  }

  &__cta {
    margin-top: 65px;
  }
}
</style>
